/* eslint-disable react/no-unknown-property */
import {
  Card,
  CardSkeleton,
  CircularGaugeChart,
  HStack,
  ICON_ALERT_CIRCLE_OUTLINE,
  ICON_BULLSEYE_ARROW,
  Message,
  Text,
} from '@doveit/bricks';
import React from 'react';
import { formatEuro } from '@doveit/hammer';
import { Agent, GOAL_METRICS } from '../../../providers/api/dtos';
import { currentYear } from '../../../utils/time/time';
import { metricLabel, statusColor } from './constants';
import * as styles from './ManagerGoalSummaryWidget.style';
import useManagerGoal from '../../hooks/use-manager-goal/useManagerGoal';
import EditManagerGoalAction from '../edit-manager-goal-action/EditManagerGoalAction';
import { SimulateManagerGoalAction } from '../simulate-manager-goal-action/SimulateManagerGoalAction';

export interface ManagerGoalSummaryWidgetProps {
  agentId: NonNullable<Agent['id']>,
}

export const LOAD_GOAL_ERROR_MESSAGE = 'Non è stato possibile caricare le informazioni degli obiettivi personali manager.';
export const NO_GOAL_MESSAGE = 'Il manager non ha ancora impostato l\'obiettivo di fatturato.';

export const ManagerGoalSummaryWidget: React.FC<ManagerGoalSummaryWidgetProps> = ({
  agentId,
}) => {
  const {
    data: existingGoal,
    isLoading: isExistingGoalLoading,
    error: existingGoalError,
    mutate: mutateExistingGoal,
  } = useManagerGoal(agentId);

  if (isExistingGoalLoading) {
    return <CardSkeleton aria-label="Obiettivi personali manager in caricamento" />;
  }

  if (existingGoalError && existingGoalError.status !== 404) {
    return (
      <Card>
        <Card.Header
          icon={{ path: ICON_ALERT_CIRCLE_OUTLINE }}
          title="Obiettivi personali manager"
          color="critical"
        />
        <Card.Content>
          <Message message={LOAD_GOAL_ERROR_MESSAGE} />
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card aria-label="Obiettivi personali manager">
      <Card.Header
        title="Obiettivi personali manager"
        icon={{ path: ICON_BULLSEYE_ARROW }}
        primaryActions={existingGoal ? [
          <EditManagerGoalAction
            agentId={agentId}
            label="Modifica"
          />,
        ] : []}
      />
      <Card.Content>
        {existingGoal
          ? (
            <div css={styles.goal}>
              <div css={styles.goalDetail}>
                <div>
                  <Text.Body>Obiettivo di fatturato {currentYear()}</Text.Body>
                  <Text.H2>{formatEuro(existingGoal.target.manager)}</Text.H2>
                </div>
              </div>
              <div css={styles.goalMetrics}>
                {GOAL_METRICS.map((metric) => {
                  const yearlyGoal = existingGoal.metrics[metric].yearly;

                  return (
                    <div
                      css={styles.metric}
                      key={metric}
                    >
                      <HStack
                        gap={150}
                        alignItems="center"
                        wrap="nowrap"
                      >
                        <CircularGaugeChart
                          progress={yearlyGoal.progress}
                          color={statusColor[yearlyGoal.status]}
                          size="S"
                          hideValue
                        />
                        <div>
                          <Text.Caption lineHeight="1">
                            {metricLabel[metric]}
                          </Text.Caption>
                          <Text.Body
                            lineHeight="1"
                            color={`${statusColor[yearlyGoal.status]!}.default.low`}
                          >
                            <strong>{yearlyGoal.performance}</strong><Text.Caption> /{Math.round(yearlyGoal.goal)}</Text.Caption>
                          </Text.Body>
                        </div>
                      </HStack>
                    </div>
                  );
                })}
              </div>
            </div>
          )
          : (
            <Message
              message={NO_GOAL_MESSAGE}
              actions={[
                <SimulateManagerGoalAction
                  agentId={agentId}
                  onSuccess={() => mutateExistingGoal()}
                  size="S"
                  emphasis="high"
                />]}
            />
          )}
      </Card.Content>
    </Card>
  );
};

export default ManagerGoalSummaryWidget;
